import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { ProjectGraphResponse } from '@avisoma/shared';
import { Button } from '../../catalyst/button';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { CO_MENTION_CAPS, CO_MENTION_CEILING, coMentionMatrix } from './coMention';
import { nodeLabel } from './graphModel';

type Props = {
  graph: ProjectGraphResponse;
  /** The selected entity. */
  entityId: string;
  /** Shows a document or an entity in the view, selects it, and pans to it. */
  onSelect: (id: string) => void;
};

const pages = (count: number) => `${count} linked ${count === 1 ? 'page' : 'pages'}`;
// Opaque, so the scrolled cells pass under the sticky column; the card's own background.
const STICKY = 'sticky left-0 z-10 bg-gray-50 dark:bg-zinc-800';
const NAME = 'text-indigo-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-indigo-400';

/**
 * The documents that mention the selected entity against the other entities they mention, with the linked pages
 * of each mention: which entities turn up together. A matrix beside the graph; the graph itself is left alone.
 */
export function CoMentionMatrix({ graph, entityId, onSelect }: Props) {
  const [showAll, setShowAll] = useState(false);
  const matrix = useMemo(
    () => coMentionMatrix(graph, entityId, showAll ? CO_MENTION_CEILING : CO_MENTION_CAPS),
    [graph, entityId, showAll],
  );
  if (matrix.totalRows === 0) return null;
  const capped = matrix.rows.length < matrix.totalRows || matrix.columns.length < matrix.totalColumns;
  const overCeiling = matrix.totalRows > CO_MENTION_CEILING.rows || matrix.totalColumns > CO_MENTION_CEILING.columns;
  const count = (value: number) => value.toLocaleString('en-US');

  return (
    <CollapsibleCard storageKey="projectGraph.coMentions" title="Co-mentions" summary={matrix.totalColumns}>
      <div className="flex flex-col gap-2 py-1 text-sm text-zinc-900 dark:text-zinc-100">
        {matrix.totalColumns === 0 ? (
          <p className="text-zinc-500 dark:text-zinc-400">These documents mention no other entity.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="border-separate border-spacing-0.5 text-xs">
              <thead>
                <tr>
                  <th scope="col" className={clsx(STICKY, 'pr-2 text-left align-bottom font-medium')}>
                    Document
                  </th>
                  {matrix.columns.map(({ node, count }) => (
                    <th key={node.id} scope="col" className="align-bottom font-normal">
                      <button
                        type="button"
                        title={`${nodeLabel(node)} · in ${count} ${count === 1 ? 'document' : 'documents'}`}
                        className={clsx(NAME, 'max-h-28 truncate [writing-mode:vertical-rl] rotate-180')}
                        onClick={() => onSelect(node.id)}
                      >
                        {nodeLabel(node)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row, r) => (
                  <tr key={row.id}>
                    <th scope="row" className={clsx(STICKY, 'pr-2 text-left font-normal')}>
                      <button
                        type="button"
                        title={row.title}
                        className={clsx(NAME, 'block max-w-32 truncate text-left')}
                        onClick={() => onSelect(row.id)}
                      >
                        {row.title}
                      </button>
                    </th>
                    {matrix.cells[r].map((value, c) => (
                      <td
                        key={matrix.columns[c].node.id}
                        title={value === null ? undefined : pages(value)}
                        className={clsx(
                          'size-6 min-w-6 rounded-sm text-center tabular-nums',
                          value !== null && 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300',
                        )}
                      >
                        {value ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {capped && !showAll && (
          <div>
            <Button plain onClick={() => setShowAll(true)}>
              {overCeiling
                ? `Show up to ${count(CO_MENTION_CEILING.rows)} × ${count(CO_MENTION_CEILING.columns)}`
                : `Show all (${count(matrix.totalRows)} × ${count(matrix.totalColumns)})`}
            </Button>
          </div>
        )}
        {capped && (showAll || overCeiling) && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {count(matrix.totalRows)} documents × {count(matrix.totalColumns)} entities in all; showing{' '}
            {count(matrix.rows.length)} × {count(matrix.columns.length)}.
          </p>
        )}
      </div>
    </CollapsibleCard>
  );
}
